import { getCurrentInstance, onBeforeUnmount } from "vue";

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_LENGTH = 2;

export interface UseDebouncedSearchOptions {
  debounceMs?: number;
  minLength?: number;
}

/**
 * Debounces a list page's search-as-you-type handler so it fires once per
 * pause in typing instead of once per keystroke (OT-4928). Preserves the
 * existing "empty or longer than minLength" gating used across list pages.
 */
export function useDebouncedSearch(
  fetchFn: () => void,
  options: UseDebouncedSearchOptions = {}
): { onSearchInput: (value: string) => void } {
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const minLength = options.minLength ?? DEFAULT_MIN_LENGTH;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  function cancel(): void {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  }

  function onSearchInput(value: string): void {
    cancel();
    if (value.length === 0 || value.length > minLength) {
      timeoutId = setTimeout(fetchFn, debounceMs);
    }
  }

  if (getCurrentInstance()) {
    onBeforeUnmount(cancel);
  }

  return { onSearchInput };
}
